import pLimit from 'p-limit';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import {
  Deployment,
  DeploymentConfiguration,
  DeploymentState,
} from 'types/deployment';

import { updateQueriesData } from 'services/update_queries_data';
import { useUpdateDeployment } from 'deployments/hooks/useUpdateDeployment';

const limit = pLimit(50);

type UseBatchUpdateDeploymentParams = Array<
  {
    id: string;
  } & (
    | {
        name: string;
        state?: DeploymentState;
        configuration?: DeploymentConfiguration;
      }
    | {
        name?: string;
        state: DeploymentState;
        configuration?: DeploymentConfiguration;
      }
    | {
        name?: string;
        state?: DeploymentState;
        configuration: DeploymentConfiguration;
      }
  )
>;

export function useBatchUpdateDeployments({
  onMutate,
  onSettled,
  ...options
}: UseMutationOptions<
  Array<Deployment>,
  APIError,
  UseBatchUpdateDeploymentParams
> = {}) {
  const queryClient = useQueryClient();
  const { mutateAsync } = useUpdateDeployment();

  return useMutation(
    async (deploymentParams) => {
      let error: APIError | undefined = undefined;

      const results = await Promise.all(
        deploymentParams.map((params) =>
          limit(() =>
            mutateAsync(params).catch((resError) => {
              // Set first error
              if (!error) {
                error = resError;
              }
            })
          )
        )
      );

      if (error) {
        throw error;
      }

      return results.filter((value) => value !== void 0);
    },
    {
      ...options,

      async onMutate(deploymentParams, ...args) {
        await queryClient.cancelQueries(['deployments']);
        await queryClient.cancelQueries(['deployment']);

        onMutate?.(deploymentParams, ...args);

        updateQueriesData<Deployment>({
          queryClient,
          listQueryKey: ['deployments'],
          singleQueryKey: ['deployment'],
          ids: deploymentParams.map(({ id }) => id),
          updateData(deployment) {
            const params = deploymentParams.find(
              ({ id }) => id === deployment.id
            );

            if (!params) {
              return deployment;
            }

            return { ...deployment, ...params };
          },
        });
      },
      onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['deployments']);
        queryClient.invalidateQueries(['deployment']);
      },
    }
  );
}
